export const PARTITION_FORMATS = ['midi', 'pdf', 'gp', 'gp3', 'gp4', 'gp5', 'gp6', 'gp7', 'gp8', 'xml', 'mxl'];
export const GUITAR_PRO_SOURCE_FORMATS = ['gp', 'gp3', 'gp4', 'gp5', 'gp6', 'gp7', 'gp8'];

export const normalizePartitionFormat = (format) => (format || '').trim().toLowerCase();

export const isGuitarProFormat = (format) => GUITAR_PRO_SOURCE_FORMATS.includes(normalizePartitionFormat(format));

export const getPartitionDownloadFormats = (partition) => {
    if (Array.isArray(partition?.available_download_formats) && partition.available_download_formats.length > 0) {
        return partition.available_download_formats;
    }
    return partition?.can_generate_exports ? ['original', 'pdf', 'gp'] : ['original'];
};

export const getPartitionStatusMeta = (partition) => {
    if (partition?.can_generate_exports) {
        return { color: 'green', label: 'Exportable' };
    }
    if (partition?.parse_status === 'failed') {
        return { color: 'orange', label: 'Parse failed' };
    }
    if (isGuitarProFormat(partition?.format)) {
        return { color: 'blue', label: 'Stored source' };
    }
    return { color: 'default', label: 'Stored as-is' };
};

export const getFileExtension = (fileName) => {
    const parts = (fileName || '').split('.');
    return parts.length > 1 ? normalizePartitionFormat(parts.pop()) : '';
};

export const getPartitionUploadHint = (fileName) => {
    return isGuitarProFormat(getFileExtension(fileName))
        ? 'Guitar Pro files are parsed after upload and can then be downloaded as PDF or GP.'
        : 'This file will be stored as-is. Automatic PDF/GP export is currently available only for Guitar Pro files.';
};

export const getPartitionFormatLabel = (partition) => {
    const format = normalizePartitionFormat(partition?.format) || getFileExtension(partition?.file_name);
    return format ? format.toUpperCase() : 'FILE';
};

export const getDownloadFormatLabel = (format, partition) => {
    switch (normalizePartitionFormat(format)) {
        case 'original': {
            const extension = normalizePartitionFormat(partition?.format) || getFileExtension(partition?.file_name);
            return extension ? `Original file (.${extension})` : 'Original file';
        }
        case 'pdf':
            return 'PDF (all tracks)';
        case 'gp':
            return 'Guitar Pro (.gp)';
        default:
            return String(format || '').toUpperCase();
    }
};

/**
 * Builds the body for the rendered-download endpoint.
 * `selection` maps track index -> { notation, tablature }. Tracks with nothing selected are dropped,
 * and tablature is never requested for non-fretted instruments.
 */
export const buildRenderedDownloadRequest = ({ tracks = [], selection = {}, conductor = false, landscape = false }) => ({
    conductor: Boolean(conductor),
    landscape: Boolean(landscape),
    tracks: tracks
        .map((track) => {
            const choice = selection[track.index] || {};
            return {
                index: track.index,
                notation: Boolean(choice.notation),
                tablature: Boolean(choice.tablature) && track.fretted !== false,
            };
        })
        .filter((track) => track.notation || track.tablature),
});

export const getDefaultTrackSelection = (tracks = []) => tracks.reduce((acc, track) => ({
    ...acc,
    [track.index]: {
        notation: track.default_notation ?? track.notation ?? true,
        tablature: track.fretted === false ? false : (track.default_tablature ?? track.tablature ?? true),
    },
}), {});

